/* eslint-disable */
import axios from 'axios'
import { hasTokenExpired } from '../auth'
import store from '../store'
import router from '../router'
import { DB_ROUTES } from '../constants'
import { getCompanyIdFromToken } from '../utils'

export const api = axios.create({
  // api .NET
  baseURL: process.env.VUE_APP_DBAPI_URL || '/api/',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
})

const IOT_API_URL = process.env.VUE_APP_IOTAPI_URL || ''
const IS_LOCAL_IOT_URL = !IOT_API_URL || /localhost|127\.0\.0\.1/i.test(IOT_API_URL)

// Public routes that don't require authentication
const PUBLIC_ROUTES = [
  DB_ROUTES.LOGIN,
  DB_ROUTES.FORGOT_PASSWORD,
  DB_ROUTES.RESET_PASSWORD
]

api.interceptors.request.use(config => {
  // Skip token check for public routes
  if (PUBLIC_ROUTES.includes(config.url)) return config

  const token = sessionStorage.token
  if (!token || hasTokenExpired(token)) {
    store.dispatch('logout')
    router.push({ name: 'Login' })
    return Promise.reject(new Error('Token não encontrado ou expirado.'))
  }

  config.headers.Authorization = `Bearer ${token}`
  
  // Automatically add CompanyId to analysis creation requests for non-SuperAdmin users
  if (config.method === 'post' && config.data) {
    const url = config.url || ''
    const isAnalysisCreation = url === 'Analyses' || 
                               url === '/api/Analyses' || 
                               url.endsWith('/Analyses') ||
                               url.includes('/Analyses') && !url.includes('/Analyses/')
    
    if (isAnalysisCreation) {
      const companyId = getCompanyIdFromToken()
      const userRole = store.state.user?.userRole
      
      // Only add CompanyId if user is not SuperAdmin and CompanyId is not already set
      if (userRole !== 'SuperAdmin' && companyId !== null && !config.data.CompanyId) {
        config.data.CompanyId = companyId
      }
    }
  }
  
  return config
}, error => Promise.reject(error))

export const apiIot = axios.create({
  // api iot
  baseURL: IOT_API_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
})

export const behavior = (method, urn, body, onSuccess, onFailure, config = {}) => { 
    // Map Request Methods
    const verbs = {
        postIot: apiIot.post,
        getIot: apiIot.get,
        deleteIot: apiIot.delete,
        post: api.post,
        put: api.put,
        get: api.get,
        delete: api.delete
    }
    let call;

    if (body) {
        call = verbs[method](urn, body, config); 
    } else {
        call = verbs[method](urn, config); 
    }
    
    call.then(response => {
        if ([200, 201, 204].includes(response.status)) {
            onSuccess(response)
        }
    }, error => {
        onFailure(error)
    })
}

// Map methods for ease function calls
export const request = {
  postIot: (urn, body, onSuccess, onFailure) => behavior('postIot', urn, body, onSuccess, onFailure),
  getIot: (urn, onSuccess, onFailure) => behavior('getIot', urn, null, onSuccess, onFailure),
  deleteIot: (urn, onSuccess, onFailure) => behavior('deleteIot', urn, null, onSuccess, onFailure),
  post: (urn, body, onSuccess, onFailure, config = {}) => behavior('post', urn, body, onSuccess, onFailure, config),  
  put: (urn, body, onSuccess, onFailure) => behavior('put', urn, body, onSuccess, onFailure),
  get: (urn, onSuccess, onFailure, config = {}) => behavior('get', urn, null, onSuccess, onFailure, config),
  delete: (urn, onSuccess, onFailure) => behavior('delete', urn, null, onSuccess, onFailure)
}

const toNormalizedBbox = (bbox) => {
  if (!Array.isArray(bbox) || bbox.length < 4) return null
  const normalized = bbox.slice(0, 4).map(value => Number(value))
  return normalized.some(Number.isNaN) ? null : normalized
}

const extractIotGrains = (payload) => {
  if (!payload) return []

  const candidateArrays = [
    payload.grains,
    payload.graos,
    payload.items,
    payload.objects,
    payload.data && payload.data.grains,
    payload.data && payload.data.graos
  ].filter(Array.isArray)

  for (const list of candidateArrays) {
    const normalized = list
      .map((item, index) => {
        const bbox = toNormalizedBbox(item && item.bbox)
        if (!bbox) return null
        return {
          index: Number(item.index ?? item.grain_index ?? item.id ?? item.object_id ?? (index + 1)),
          bbox,
          predict: item.predict ?? item.prediction ?? item.class ?? '',
          target: item.target ?? null
        }
      })
      .filter(Boolean)

    if (normalized.length > 0) return normalized
  }

  const numericEntries = Object.entries(payload)
    .filter(([key, value]) => !Number.isNaN(Number(key)) && value && typeof value === 'object')
    .map(([key, value]) => {
      const bbox = toNormalizedBbox(value.bbox)
      if (!bbox) return null
      return {
        index: Number(value.index ?? value.grain_index ?? value.id ?? value.object_id ?? Number(key)),
        bbox,
        predict: value.predict ?? value.prediction ?? value.class ?? '',
        target: value.target ?? null
      }
    })
    .filter(Boolean)

  return numericEntries
}

const parseSampleId = (sampleId) => {
  if (!sampleId || typeof sampleId !== 'string') return null
  const parts = sampleId.split(/[-_/]/).map(part => part.trim()).filter(Boolean)
  if (parts.length < 3) return null
  return {
    classe: parts[0],
    corte: parts[1],
    remessa: parts[2],
    analise: sampleId
  }
}

const getIotGrainsByAnalysisId = async (analysisId) => {
  if (IS_LOCAL_IOT_URL) return []

  try {
    const analysisResponse = await api.get(DB_ROUTES.GET_ANALYSIS_BY_ID(analysisId))
    const analysis = analysisResponse.data || {}
    const sampleId = analysis.sampleId || analysis.sample_Id || analysis.SampleId
    const params = parseSampleId(sampleId)
    if (!params) return []

    const iotResponse = await apiIot.get('/analisar', { params })
    return extractIotGrains(iotResponse.data)
  } catch (error) {
    return []
  }
}

const extractApiErrorMessage = (error) => {
  const data = error && error.response && error.response.data
  if (!data) return null
  if (typeof data === 'string') return data
  return data.error || data.message || data.title || null
}

export const getAnaliseCompleta = async (analysisId) => {
  let imageResponse
  let detailsResponse
  let grainsResponse
  let iotGrains = []

  try {
    ;[imageResponse, detailsResponse, grainsResponse, iotGrains] = await Promise.all([
      api.get(DB_ROUTES.GET_AWS_PATH(analysisId, 'image'), { responseType: 'blob' }),
      api.get(DB_ROUTES.GET_ANALYSIS_DETAILS(analysisId)),
      api.get(DB_ROUTES.GET_ANALYSIS_GRAINS(analysisId)),
      getIotGrainsByAnalysisId(analysisId)
    ])
  } catch (error) {
    const message = extractApiErrorMessage(error)
    const err = new Error(
      message || 'Análise não finalizada ou laudo inválido. Não foi possível carregar os dados.'
    )
    err.status = error && error.response && error.response.status
    throw err
  }

  const imageBlob = imageResponse.data
  const imageUrl = URL.createObjectURL(imageBlob)
  const payload = detailsResponse.data || {}
  const details = payload.details || payload
  const excludedKeys = ['gross_sample_weight', 'analysesId', 'total_damaged', 'total_damaged_percentage']
  const grains = Array.isArray(payload.grains)
    ? payload.grains
    : Object.entries(details || {})
      .filter(([key]) => !excludedKeys.includes(key))
      .map(([key, value], index) => ({
        index: index + 1,
        key,
        ...value
      }))

  const apiGrains = Array.isArray(grainsResponse.data) ? grainsResponse.data : []
  const normalizedGrains = apiGrains.length > 0
    ? apiGrains
    : (iotGrains.length > 0 ? iotGrains : grains)

  return {
    analysisId,
    imageBlob,
    imageUrl,
    details,
    grains: normalizedGrains
  }
}
